import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Search, Info, Users, Radar as RadarIcon, LayoutGrid, GraduationCap, ChevronRight, ChevronLeft, Eye, X } from 'lucide-react';
import { fetchCampagnesRequest } from '../../../redux/actions/campagnesActions';
import { fetchManagerInboxRequest } from '../../../redux/actions/skillEvaluationActions';
import { fetchAllFormationSkillsRequest } from '../../../redux/actions/formationSkillActions';
import { fetchDomainesRequest, fetchThemesRequest } from '../../../redux/actions/paramDomainesThemesActions';
import SkillRadar from '../components/SkillRadar';
import Skill4Box from '../components/Skill4Box';
import Team4Box from '../components/Team4Box';
import { buildDomainThemeRadarOptions, buildRadarFilterOptions, filterRadarData, filterUsersByRadarFilters, isSkillsCampaign, RadarFiltersPanel, toRadarSkill } from '../radarFilters';
import '../EvaluationDesign.css';

const toGlobalManagerTab = (tab) => ({
    radar: 'radars',
    '4box': '4box',
    plan: 'plans',
}[tab] || 'radars');

const toIndividualManagerTab = (tab) => ({
    radars: 'radar',
    '4box': '4box',
    plans: 'plan',
}[tab] || 'radar');

const UserTrainingPlanView = ({ radarData, allMappings }) => {
    return (
        <div className="eval-plan-container">
            <div className="eval-plan-header">
                <h3><GraduationCap size={18} /> Plan de Formation Personnalisé</h3>
                <p>Formations recommandées pour combler les écarts de compétences identifiés.</p>
            </div>

            <div className="eval-plan-list">
                {radarData.filter(s => s.requis > s.obtenu).length > 0 ? (
                    radarData.filter(s => s.requis > s.obtenu).map(skill => {
                        const recommendations = allMappings.filter(m => m.skillId === skill.id);
                        const gap = skill.requis - skill.obtenu;
                        const labelsList = ["Basique", "Intermédiaire", "Avancé", "Confirmé", "Expert"];
                        const maxSteps = Math.min(Math.max(skill.requis, 1), 5); 
                        const stepLabels = labelsList.slice(0, maxSteps);
                        
                        return (
                            <div key={skill.id} className="eval-plan-card-modern">
                                <div className="eval-plan-skill-title">{skill.skill}</div>
                                
                                <div className="eval-stepper">
                                    {stepLabels.map((lbl, idx) => {
                                        const isCompleted = idx < Math.floor(skill.obtenu);
                                        const isLineGreen = (idx + 1) < Math.floor(skill.obtenu);
                                        const isLast = idx === stepLabels.length - 1;
                                        
                                        return (
                                            <React.Fragment key={idx}>
                                                <div className="eval-step-node">
                                                    <div className={`eval-step-circle ${isCompleted ? 'completed' : ''}`}>
                                                        {idx + 1}
                                                    </div>
                                                    <div className={`eval-step-label ${isCompleted ? 'completed' : ''}`}>
                                                        {lbl}
                                                    </div>
                                                </div>
                                                {!isLast && (
                                                    <div className={`eval-step-bar ${isLineGreen ? 'completed' : ''}`}></div>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </div>

                                <div className="eval-plan-recommendations-section">
                                    <div className="eval-plan-rec-title">Formations nécessaires</div>
                                    
                                    {recommendations.length > 0 ? recommendations.map(rec => (
                                        <div key={rec.id} className="eval-rec-row">
                                            <div className="eval-rec-name">{rec.formationNom}</div>
                                            <div className="eval-rec-progress">
                                                <svg viewBox="0 0 36 36" className="circular-chart blue">
                                                    <path className="circle-bg"
                                                        d="M18 2.0845
                                                        a 15.9155 15.9155 0 0 1 0 31.831
                                                        a 15.9155 15.9155 0 0 1 0 -31.831"
                                                    />
                                                    <path className="circle"
                                                        strokeDasharray={`${rec.pourcentage}, 100`}
                                                        d="M18 2.0845
                                                        a 15.9155 15.9155 0 0 1 0 31.831
                                                        a 15.9155 15.9155 0 0 1 0 -31.831"
                                                    />
                                                    <text x="18" y="20.35" className="percentage">{rec.pourcentage}%</text>
                                                </svg>
                                            </div>
                                        </div>
                                    )) : (
                                        <div className="eval-rec-empty">
                                            Aucune formation associée à cette compétence.
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="eval-plan-completed">
                        <div className="eval-success-icon">✓</div>
                        <h4>Félicitations !</h4>
                        <p>Ce collaborateur possède ou dépasse tous les niveaux requis pour son poste.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default function ManagerRadarPage() {
    const dispatch = useDispatch();
    const { list: campagnesList } = useSelector(state => state.campagnes || { list: [] });
    const { inboxManager, loading } = useSelector(state => state.skillEvaluation || { inboxManager: [], loading: false });
    const { allMappings } = useSelector(state => state.formationSkill || { allMappings: [] });
    const { domaines = [], themes = [] } = useSelector(state => state.paramDomainesThemes || {});

    const [selectedCampagneId, setSelectedCampagneId] = useState("");
    const [selectedUser, setSelectedUser] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [activeTab, setActiveTab] = useState("radar"); // "radar" or "4box"
    const [globalActiveTab, setGlobalActiveTab] = useState("radars"); // "radars", "4box", "plans"
    const [currentRadarIndex, setCurrentRadarIndex] = useState(0);
    const [planModalUser, setPlanModalUser] = useState(null);
    const [radarFilters, setRadarFilters] = useState({
        entity: '',
        poste: '',
        domaine: '',
        theme: '',
        competence: '',
    });

    useEffect(() => {
        dispatch(fetchCampagnesRequest());
        dispatch(fetchManagerInboxRequest());
        dispatch(fetchAllFormationSkillsRequest());
        dispatch(fetchDomainesRequest());
        dispatch(fetchThemesRequest());
    }, [dispatch]);

    const team = useMemo(() => {
        return (inboxManager || []).map(e => ({
            ...e,
            initiales: e.userFullName ? e.userFullName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) : "??",
            nom: e.userFullName,
            color: '#2f4595'
        }));
    }, [inboxManager]);

    const radarFilterOptions = useMemo(() => {
        const radarOptions = buildRadarFilterOptions(team);
        return {
            ...radarOptions,
            ...buildDomainThemeRadarOptions(domaines, themes),
            competences: radarOptions.competences,
        };
    }, [team, domaines, themes]);

    const filteredTeam = useMemo(() => {
        return filterUsersByRadarFilters(team, { ...radarFilters, search: searchQuery }).filter(u => {
            const matchCampagne = !selectedCampagneId || u.campagneId === parseInt(selectedCampagneId);
            return matchCampagne;
        });
    }, [team, radarFilters, searchQuery, selectedCampagneId]);

    const collaboratorOptions = useMemo(() => filteredTeam.map((user) => ({
        value: user.id?.toString(),
        label: `${user.nom}${user.poste ? ` - ${user.poste}` : ''}`,
    })), [filteredTeam]);

    const handleCollaboratorFilterChange = (value) => {
        if (!value) {
            setGlobalActiveTab(toGlobalManagerTab(activeTab));
            setSelectedUser(null);
            return;
        }

        const user = filteredTeam.find((u) => u.id?.toString() === value);
        setActiveTab(toIndividualManagerTab(globalActiveTab));
        setSelectedUser(user || null);
    };

    useEffect(() => {
        if (selectedUser && !filteredTeam.some((u) => u.id === selectedUser.id)) {
            setGlobalActiveTab(toGlobalManagerTab(activeTab));
            setSelectedUser(null);
        }
    }, [activeTab, filteredTeam, selectedUser]);

    useEffect(() => {
        setCurrentRadarIndex(0);
    }, [selectedCampagneId, searchQuery, radarFilters]);

    const activeCampagnes = useMemo(() => 
        (campagnesList || []).filter(c => c.status === true && isSkillsCampaign(c)), 
    [campagnesList]);

    useEffect(() => {
        if (!selectedCampagneId && activeCampagnes.length > 0) {
            const defaultCamp = activeCampagnes.find(c => c.isDefault);
            if (defaultCamp) setSelectedCampagneId(defaultCamp.id.toString());
        }
    }, [activeCampagnes, selectedCampagneId]);

    const radarData = useMemo(() => {
        if (!selectedUser || !selectedUser.niveaux) return [];

        return filterRadarData(selectedUser.niveaux.map(n => toRadarSkill(n)), radarFilters);
    }, [selectedUser, radarFilters]);

    return (
        <div className="eval-page eval-radar-page eval-rf-radar-page">
            <div className="eval-header eval-radar-header">
                {/*<div>*/}
                {/*    <h1>Radars de Compétences Équipe</h1>*/}
                {/*    <p>Visualisez graphiquement l'adéquation profil/poste pour chaque membre de votre équipe.</p>*/}
                {/*</div>*/}
                <label className="eval-rf-campaign-top">
                    <span>Campagne</span>
                    <select value={selectedCampagneId} onChange={(event) => setSelectedCampagneId(event.target.value)}>
                        <option value="">Toutes les campagnes</option>
                        {(activeCampagnes || []).map((campaign) => (
                            <option key={campaign.id} value={campaign.id?.toString()}>
                                {campaign.nom || campaign.name || 'Campagne'}{campaign.anneeCampagne ? ` (${campaign.anneeCampagne})` : ''}
                            </option>
                        ))}
                    </select>
                </label>
            </div>

            <div className="eval-radar-tabs-bar">
                {!selectedUser ? (
                    <div className="eval-radar-top-tabs">
                        <button className={globalActiveTab === 'radars' ? 'active' : ''} onClick={() => setGlobalActiveTab('radars')}><RadarIcon size={14} /> Radar</button>
                        <button className={globalActiveTab === '4box' ? 'active' : ''} onClick={() => setGlobalActiveTab('4box')}><LayoutGrid size={14} /> 4-Box</button>
                        <button className={globalActiveTab === 'plans' ? 'active' : ''} onClick={() => setGlobalActiveTab('plans')}><GraduationCap size={14} /> Plans de formation</button>
                    </div>
                ) : (
                    <div className="eval-radar-top-tabs">
                        <button className={activeTab === 'radar' ? 'active' : ''} onClick={() => setActiveTab('radar')}><RadarIcon size={14} /> Radar</button>
                        <button className={activeTab === '4box' ? 'active' : ''} onClick={() => setActiveTab('4box')}><LayoutGrid size={14} /> 4-Box</button>
                        <button className={activeTab === 'plan' ? 'active' : ''} onClick={() => setActiveTab('plan')}><GraduationCap size={14} /> Plans de formation</button>
                    </div>
                )}
            </div>

            <RadarFiltersPanel
                filters={radarFilters}
                setFilters={setRadarFilters}
                options={radarFilterOptions}
                className="eval-manager-radar-filters"
                collaboratorValue={selectedUser?.id?.toString() || ''}
                onCollaboratorChange={handleCollaboratorFilterChange}
                collaboratorOptions={collaboratorOptions}
                hideEntityFilter
            />

            <div className="eval-layout">
                {false && <aside className="eval-sidebar">
                    <div className="eval-toolbar">
                        <div className="eval-search-box">
                            <Search className="eval-search-icon" size={16} />
                            <input 
                                type="text" 
                                className="eval-input" 
                                placeholder="Chercher un collaborateur..." 
                                value={searchQuery} 
                                onChange={(e) => setSearchQuery(e.target.value)} 
                            />
                        </div>
                    </div>
                    <div className="eval-list">
                        <div 
                            className={`eval-user-card ${selectedUser === null ? 'active' : ''}`} 
                            onClick={() => setSelectedUser(null)}
                            style={{ borderBottom: '2px solid #e2e8f0', marginBottom: '10px', backgroundColor: selectedUser === null ? '#f1f5f9' : 'transparent' }}
                        >
                            <div className="eval-avatar" style={{ backgroundColor: '#94a3b8' }}><Users size={20} color="white" /></div>
                            <div className="eval-user-info">
                                <div className="eval-user-name" style={{ fontWeight: '600' }}>Vue Générale</div>
                                <div className="eval-user-sub">Dashboard Équipe</div>
                            </div>
                        </div>

                        {loading && team.length === 0 ? (
                            <div className="eval-empty-small">Chargement...</div>
                        ) : filteredTeam.length > 0 ? filteredTeam.map(u => (
                            <div key={u.id} className={`eval-user-card ${selectedUser?.id === u.id ? 'active' : ''}`} onClick={() => setSelectedUser(u)}>
                                <div className="eval-avatar" style={{ backgroundColor: u.color }}>{u.initiales}</div>
                                <div className="eval-user-info">
                                    <div className="eval-user-name">{u.nom}</div>
                                    <div className="eval-user-sub">{u.campagneNom}</div>
                                </div>
                            </div>
                        )) : (
                            <div className="eval-empty-small">Aucun collaborateur trouvé</div>
                        )}
                    </div>
                </aside>}

                <main className="eval-main">
                    {!selectedUser ? (
                        <div className="eval-content">
                            <div className="eval-card" style={{ textAlign: 'center' }}>
                                {/*<div className="eval-section-title" style={{ justifyContent: 'center' }}>*/}
                                {/*    <Users size={18} /> Vue Générale*/}
                                {/*</div>*/}
                                
                                <div className="eval-content-tabs">
                                    <button 
                                        className={`eval-content-tab ${globalActiveTab === 'radars' ? 'active' : ''}`}
                                        onClick={() => setGlobalActiveTab('radars')}
                                    >
                                        <RadarIcon size={14} /> Radars Globaux
                                    </button>
                                    <button 
                                        className={`eval-content-tab ${globalActiveTab === '4box' ? 'active' : ''}`}
                                        onClick={() => setGlobalActiveTab('4box')}
                                    >
                                        <LayoutGrid size={14} /> Matrice 4-Box
                                    </button>
                                    <button 
                                        className={`eval-content-tab ${globalActiveTab === 'plans' ? 'active' : ''}`}
                                        onClick={() => setGlobalActiveTab('plans')}
                                    >
                                        <GraduationCap size={14} /> Plans de Formation
                                    </button>
                                </div>

                                {globalActiveTab === 'radars' ? (
                                    <div className="eval-general-radars">
                                        {filteredTeam.length > 0 ? (() => {
                                            const currentUser = filteredTeam[currentRadarIndex];
                                            if (!currentUser) return null;
                                            
                                            // Get radarData for currentUser
                                            const cRadarData = currentUser.niveaux ? filterRadarData(currentUser.niveaux.map(n => toRadarSkill(n)), radarFilters) : [];

                                            return (
                                                <div style={{ position: 'relative', marginTop: '20px' }}>
                                                    <div className="eval-rf-radar-nav">
                                                        <button 
                                                            onClick={() => setCurrentRadarIndex(prev => prev > 0 ? prev - 1 : filteredTeam.length - 1)}
                                                            className="eval-rf-radar-nav-btn"
                                                        >
                                                            <ChevronLeft size={16} />
                                                        </button>
                                                        
                                                        <div className="eval-rf-radar-nav-count">
                                                            {currentRadarIndex + 1} / {filteredTeam.length}
                                                        </div>

                                                        <button 
                                                            onClick={() => setCurrentRadarIndex(prev => prev < filteredTeam.length - 1 ? prev + 1 : 0)}
                                                            className="eval-rf-radar-nav-btn"
                                                        >
                                                            <ChevronRight size={16} />
                                                        </button>
                                                    </div>
                                                    
                                                    {cRadarData.length > 0 ? (
                                                        <SkillRadar data={cRadarData} title={currentUser.nom} subtitle={currentUser.poste || 'Membre de l\'équipe'} />
                                                    ) : (
                                                        <div className="eval-empty-small" style={{ margin: '40px 0' }}>Aucune donnée radar pour ce collaborateur.</div>
                                                    )}
                                                    
                                                </div>
                                            );
                                        })() : (
                                            <div className="eval-empty-small">Aucun collaborateur à afficher.</div>
                                        )}
                                    </div>
                                ) : globalActiveTab === '4box' ? (
                                    <div style={{ marginTop: '20px' }}>
                                        <Team4Box teamData={filteredTeam} onUserSelect={setSelectedUser} />
                                    </div>
                                ) : (
                                    <div className="eval-general-plans">
                                        <div style={{ overflowX: 'auto', marginTop: '20px' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                                                <thead>
                                                    <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                                                        <th style={{ padding: '12px 8px', color: '#64748b', fontSize: '13px', fontWeight: '600' }}>Collaborateur</th>
                                                        <th style={{ padding: '12px 8px', color: '#64748b', fontSize: '13px', fontWeight: '600' }}>Poste</th>
                                                        <th style={{ padding: '12px 8px', color: '#64748b', fontSize: '13px', fontWeight: '600', textAlign: 'center' }}>A besoin de formation</th>
                                                        <th style={{ padding: '12px 8px', color: '#64748b', fontSize: '13px', fontWeight: '600', textAlign: 'center' }}>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredTeam.map(u => {
                                                        const cRadarData = u.niveaux ? filterRadarData(u.niveaux.map(n => toRadarSkill(n)), radarFilters) : [];
                                                        const hasGap = cRadarData.some(s => s.requis > s.obtenu);
                                                        
                                                        return (
                                                            <tr key={u.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                                <td style={{ padding: '12px 8px', fontWeight: '500', color: '#0f172a' }}>{u.nom}</td>
                                                                <td style={{ padding: '12px 8px', color: '#64748b', fontSize: '13px' }}>{u.poste || '-'}</td>
                                                                <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                                                                    <span style={{ 
                                                                        padding: '4px 8px', 
                                                                        borderRadius: '4px', 
                                                                        fontSize: '12px', 
                                                                        fontWeight: '500', 
                                                                        backgroundColor: hasGap ? '#fee2e2' : '#dcfce7', 
                                                                        color: hasGap ? '#dc2626' : '#16a34a' 
                                                                    }}>
                                                                        {hasGap ? 'Oui' : 'Non'}
                                                                    </span>
                                                                </td>
                                                                <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                                                                    <button 
                                                                        onClick={() => setPlanModalUser(u)}
                                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2f478f' }}
                                                                        title="Voir le plan de formation"
                                                                    >
                                                                        <Eye size={18} />
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                    {filteredTeam.length === 0 && (
                                                        <tr>
                                                            <td colSpan="4" style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>Aucun collaborateur trouvé.</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : radarData.length > 0 ? (
                        <div className="eval-content">
                            <div className="eval-card" style={{ textAlign: 'center' }}>
                                <div className="eval-section-title" style={{ justifyContent: 'center' }}>
                                    {activeTab === 'radar' ? <RadarIcon size={18} /> : <LayoutGrid size={18} />}
                                    {activeTab === 'radar' ? "Radar" : "Matrice 4-Box"} - {selectedUser.nom}
                                </div>

                                {/* Tabs Switcher */}
                                <div className="eval-content-tabs">
                                    <button 
                                        className={`eval-content-tab ${activeTab === 'radar' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('radar')}
                                    >
                                        <RadarIcon size={14} /> Radar
                                    </button>
                                    <button 
                                        className={`eval-content-tab ${activeTab === '4box' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('4box')}
                                    >
                                        <LayoutGrid size={14} /> Matrice 4-Box
                                    </button>
                                    <button 
                                        className={`eval-content-tab ${activeTab === 'plan' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('plan')}
                                    >
                                        <GraduationCap size={14} /> Plan de Formation
                                    </button>
                                </div>

                                {activeTab === 'radar' ? (
                                    <>
                                        <SkillRadar data={radarData} title={selectedUser.nom} subtitle={selectedUser.poste || 'Membre de l\'équipe'} />
                                        {/*<div style={{ marginTop: '24px', fontSize: '13px', color: '#64748b' }}>*/}
                                        {/*    Le polygone <strong>bleu</strong> représente les compétences acquises.<br/>*/}
                                        {/*    La bordure <strong>corail</strong> représente le niveau attendu par le poste.*/}
                                        {/*</div>*/}
                                    </>
                                ) : activeTab === '4box' ? (
                                    <Skill4Box data={radarData} />
                                ) : (
                                    <UserTrainingPlanView radarData={radarData} allMappings={allMappings} />
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="eval-empty">
                            <Info size={64} className="eval-empty-icon" />
                            <h3>Aucune donnée</h3>
                            <p>Aucune évaluation validée n'a été trouvée pour ce collaborateur.</p>
                        </div>
                    )}
                </main>
            </div>

            {/* Plan de Formation Modal */}
            {planModalUser && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
                    backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
                     onMouseDown={(event) => event.target === event.currentTarget && setPlanModalUser(null)}
                >
                    <div style={{
                        backgroundColor: 'white', borderRadius: '8px', width: '90%', maxWidth: '800px', 
                        maxHeight: '90vh', overflowY: 'auto', padding: '24px', position: 'relative',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    }}>
                        <button 
                            onClick={() => setPlanModalUser(null)}
                            style={{ position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                        >
                            <X size={24} color="#64748b" />
                        </button>
                        
                        <div style={{ marginBottom: '20px', borderBottom: '1px solid #e2e8f0', paddingBottom: '16px' }}>
                            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#0f172a', margin: 0 }}>
                                Plan de formation - {planModalUser.nom}
                            </h2>
                            <p style={{ color: '#64748b', margin: '4px 0 0 0', fontSize: '14px' }}>{planModalUser.poste || 'Membre de l\'équipe'}</p>
                        </div>

                        <UserTrainingPlanView 
                            radarData={planModalUser.niveaux ? filterRadarData(planModalUser.niveaux.map(n => toRadarSkill(n)), radarFilters) : []} 
                            allMappings={allMappings} 
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
